<template>
  <page>
    <lazy-div>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Co / RI Panel</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Co / RI Panel</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">Query Co / RI Panel</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout v-if="newMode" row wrap>
              <v-flex xs6>
                <v-text-field
                  readonly
                  v-model="form.scheme"
                  :label="labels.scheme"
                  :error-messages="errors.collect('scheme')"
                  data-vv-name="scheme"
                  :data-vv-as="labels.scheme"
                  v-validate="'required|max:4|param'"
                  append-icon="mdi-magnify"
                  @click:append="getHelpList('scheme')"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-show="schemeDescFromHelpList != undefined && schemeDescFromHelpList != ''">
                <v-subheader class="queryLabel">{{ labels.schemeDescFromHelpList }}</v-subheader>
                <div class="queryValue">{{ schemeDescFromHelpList }}</div>
              </v-flex>
            </v-layout>
            <v-layout v-else row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.scheme }}</v-subheader>
                <div class="queryValue">{{ form.scheme }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.schemeDescFromDB }}</v-subheader>
                <div class="queryValue">{{ schemeDescFromDB }}</div>
              </v-flex>
            </v-layout>

            <!-- EffDate -->
            <v-layout row wrap>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.effDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(cloneQueryEffDate) }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <cal-picker
                  :label="labels.effDate"
                  v-model="form.effDate"
                  :error-messages="errors.collect('effDate')"
                  data-vv-name="effDate"
                  :data-vv-as="labels.effDate"
                  v-validate="effDateValidation"
                ></cal-picker>
              </v-flex>
            </v-layout>

            <!-- expDate -->
            <v-layout row wrap>
              <v-flex xs6>
                <cal-picker
                  :label="labels.expDate"
                  v-model="form.expDate"
                  :error-messages="errors.collect('expDate')"
                  data-vv-name="expDate"
                  :data-vv-as="labels.expDate"
                  v-validate="expDateValidation"
                ></cal-picker>
              </v-flex>
            </v-layout>

            <!-- polType -->
            <v-layout v-if="!newMode" row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.polType }}</v-subheader>
                <div class="queryValue">{{ form.polType }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.polTypeDescFromDB }}</v-subheader>
                <div class="queryValue">{{ polTypeDescFromDB }}</div>
              </v-flex>
            </v-layout>
            <v-layout v-else row wrap>
              <v-flex xs6>
                <v-text-field
                  readonly
                  v-model="form.polType"
                  :label="labels.polType"
                  :error-messages="errors.collect('polType')"
                  data-vv-name="polType"
                  :data-vv-as="labels.polType"
                  v-validate="'required|max:4'"
                  append-icon="mdi-magnify"
                  @click:append="getHelpList('polType')"
                  :disabled="this.form.scheme === '' || this.form.scheme === undefined"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-show="polTypeDescFromHelpList != undefined && polTypeDescFromHelpList != ''">
                <v-subheader class="queryLabel">{{ labels.polTypeDescFromHelpList }}</v-subheader>
                <div class="queryValue">{{ polTypeDescFromHelpList }}</div>
              </v-flex>
            </v-layout>

            <!-- RI Type -->
            <v-layout row wrap>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.riType }}</v-subheader>
                <div class="queryValue" v-for="item in filterRiTypeList" :key="item.code">{{ item.value }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.riType"
                  :items="riTypeList"
                  v-model="form.riType"
                  item-text="value"
                  item-value="code"
                  data-vv-name="riType"
                  :data-vv-as="labels.riType"
                  :error-messages="errors.collect('riType')"
                  v-validate="'required|max:1'"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- riCo -->
            <v-layout row wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.riCo }}</v-subheader>
                <div class="queryValue">{{ form.riCo }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.riCoDescFromDB }}</v-subheader>
                <div class="queryValue">{{ riCoDescFromDB }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-else>
              <v-flex xs6>
                <v-text-field
                  readonly
                  v-model="form.riCo"
                  :label="labels.riCo"
                  :error-messages="errors.collect('riCo')"
                  data-vv-name="riCo"
                  :data-vv-as="labels.riCo"
                  v-validate="'required|max:8'"
                  append-icon="mdi-magnify"
                  @click:append="getHelpList('riCo')"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-show="riCoDescFromHelpList != undefined && riCoDescFromHelpList != ''">
                <v-subheader class="queryLabel">{{ labels.riCoDescFromHelpList }}</v-subheader>
                <div class="queryValue">{{ riCoDescFromHelpList }}</div>
              </v-flex>
            </v-layout>

            <!-- acctNoSfx -->
            <v-layout row wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.acctNoSfx }}</v-subheader>
                <div class="queryValue">{{ form.acctNoSfx }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.acctNoSfxDescFromDB }}</v-subheader>
                <div class="queryValue">{{ acctNoSfxDescFromDB }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-else>
              <v-flex xs6>
                <v-text-field
                  readonly
                  v-model="form.acctNoSfx"
                  :label="labels.acctNoSfx"
                  :error-messages="errors.collect('acctNoSfx')"
                  data-vv-name="acctNoSfx"
                  :data-vv-as="labels.acctNoSfx"
                  v-validate="'max:4'"
                  append-icon="mdi-magnify"
                  @click:append="getHelpList('acctNoSfx')"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-show="acctNoSfxDescFromHelpList != undefined && acctNoSfxDescFromHelpList != ''">
                <v-subheader class="queryLabel">{{ labels.acctNoSfxDescFromHelpList }}</v-subheader>
                <div class="queryValue">{{ acctNoSfxDescFromHelpList }}</div>
              </v-flex>
            </v-layout>

            <!-- schemeDesc -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.schemeDesc }}</v-subheader>
                <div class="queryValue">{{ form.schemeDesc }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.schemeDesc"
                  :label="labels.schemeDesc"
                  :error-messages="errors.collect('schemeDesc')"
                  data-vv-name="schemeDesc"
                  :data-vv-as="labels.schemeDesc"
                  v-validate="'max:40'"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- riSipct -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.riSipct }}</v-subheader>
                <div class="queryValue">{{ form.riSipct }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.riSipct"
                  :label="labels.riSipct"
                  :error-messages="errors.collect('riSipct')"
                  data-vv-name="riSipct"
                  :data-vv-as="labels.riSipct"
                  v-validate="riSIPctTotalValidation"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- comm1 -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.comm1pct }}</v-subheader>
                <div class="queryValue">{{ form.comm1pct }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.comm1pct"
                  :label="labels.comm1pct"
                  :error-messages="errors.collect('comm1pct')"
                  data-vv-name="comm1pct"
                  :data-vv-as="labels.comm1pct"
                  v-validate="'mask_decimal:6|mask_min_value:0.000000'"
                  v-num-mask="{ negative: true, intScale: 18, decScale: 6 }"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- comm2 -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.comm2pct }}</v-subheader>
                <div class="queryValue">{{ form.comm2pct }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.comm2pct"
                  :label="labels.comm2pct"
                  :error-messages="errors.collect('comm2pct')"
                  data-vv-name="comm2pct"
                  :data-vv-as="labels.comm2pct"
                  v-validate="'mask_decimal:6|mask_min_value:0.000000'"
                  v-num-mask="{ negative: true, intScale: 18, decScale: 6 }"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.comm2gn }}</v-subheader>
                <div class="queryValue" v-for="item in filterComm2gnList" :key="item.code">{{ item.value }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.comm2gn"
                  :items="comm2gnList"
                  v-model="form.comm2gn"
                  item-text="value"
                  item-value="code"
                  data-vv-name="comm2gn"
                  :data-vv-as="labels.comm2gn"
                  :error-messages="errors.collect('comm2gn')"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- comm3 -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.comm3pct }}</v-subheader>
                <div class="queryValue">{{ form.comm3pct }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.comm3pct"
                  :label="labels.comm3pct"
                  :error-messages="errors.collect('comm3pct')"
                  data-vv-name="comm3pct"
                  :data-vv-as="labels.comm3pct"
                  v-validate="'mask_decimal:6|mask_min_value:0.000000'"
                  v-num-mask="{ negative: true, intScale: 18, decScale: 6 }"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.comm3gn }}</v-subheader>
                <div class="queryValue" v-for="item in filterComm3gnList" :key="item.code">{{ item.value }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.comm3gn"
                  :items="comm3gnList"
                  v-model="form.comm3gn"
                  item-text="value"
                  item-value="code"
                  data-vv-name="comm3gn"
                  :data-vv-as="labels.comm3gn"
                  :error-messages="errors.collect('comm3gn')"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- cessStamp -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.cessStamp }}</v-subheader>
                <div class="queryValue">{{ form.cessStamp }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.cessStamp"
                  :label="labels.cessStamp"
                  :error-messages="errors.collect('cessStamp')"
                  data-vv-name="cessStamp"
                  :data-vv-as="labels.cessStamp"
                  v-validate="'mask_decimal:4|mask_min_value:0.0000'"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- riLevy -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.riLevy }}</v-subheader>
                <div class="queryValue">{{ form.riLevy }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.riLevy"
                  :label="labels.riLevy"
                  :error-messages="errors.collect('riLevy')"
                  data-vv-name="riLevy"
                  :data-vv-as="labels.riLevy"
                  v-validate="'mask_decimal:4|mask_min_value:0.0000'"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- appAcqCost -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.appAcqCost" :label="labels.appAcqCost"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.appAcqCost"
                  :label="labels.appAcqCost"
                  :error-messages="errors.collect('appAcqCost')"
                  data-vv-name="appAcqCost"
                  :data-vv-as="labels.appAcqCost"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>
            </v-layout>

            <!-- recip -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.recip }}</v-subheader>
                <div class="queryValue" v-for="item in filterRecipList" :key="item.code">{{ item.value }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.recip"
                  :items="recipList"
                  v-model="form.recip"
                  item-text="value"
                  item-value="code"
                  data-vv-name="recip"
                  :data-vv-as="labels.recip"
                  :error-messages="errors.collect('recip')"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- tranComDate -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.tranComDate }}</v-subheader>
                <div class="queryValue" v-for="item in filterTranComDateList" :key="item.code">{{ item.value }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.tranComDate"
                  :items="tranComDateList"
                  v-model="form.tranComDate"
                  item-text="value"
                  item-value="code"
                  data-vv-name="tranComDate"
                  :data-vv-as="labels.tranComDate"
                  :error-messages="errors.collect('tranComDate')"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- bordereaux -->
            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.bordereaux" :label="labels.bordereaux"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.bordereaux"
                  :label="labels.bordereaux"
                  :error-messages="errors.collect('bordereaux')"
                  data-vv-name="bordereaux"
                  :data-vv-as="labels.bordereaux"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>
            </v-layout>
          </v-container>
          <v-container fluid grid-list-lg>
            <v-layout>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ modifiedBy }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(modifiedDate) }}</div>
              </v-flex>
              <v-flex xs12>
                <v-btn class="mr-2" color="error" @click="doCancel" :loading="submitting">
                  <span>Cancel</span>
                  <v-icon right dark>mdi-cancel</v-icon>
                </v-btn>
                <v-btn :disabled="queryMode" color="primary" dark class="mr-2" @click="doSave" :loading="submitting">
                  <span>Save</span>
                  <v-icon right>mdi-content-save</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
          <v-dialog v-model="schemeList" width="900px" persistent>
            <scheme-help-list v-if="schemeHelpList" :sch="form.scheme" @close="doClose" @doAssign="doAssign($event)"></scheme-help-list>
          </v-dialog>
          <v-dialog v-model="polTypeList" width="900px" persistent>
            <pol-type-help-list
              v-if="polTypeHelpList"
              :pt="form.polType"
              :sch="form.scheme"
              @close="doClose"
              @doAssign="doAssign($event)"
            ></pol-type-help-list>
          </v-dialog>
          <v-dialog v-model="riCoList" width="900px" persistent>
            <ri-co-help-list v-if="riCoHelpList" :riType="form.riType" @close="doClose" @doAssign="doAssign($event)"></ri-co-help-list>
          </v-dialog>
          <v-dialog v-model="acctNoSfxList" width="900px" persistent>
            <acct-no-sfx-help-list v-if="acctNoSfxHelpList" @close="doClose" @doAssign="doAssign($event)"></acct-no-sfx-help-list>
          </v-dialog>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { formatDisplayDate, removeTime } from '@/utils/date'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { expandURI } from '@/utils/ajax'
import SchemeHelpList from './SchemeHelpList.vue'
import PolTypeHelpList from './PolTypeHelpList.vue'
import RiCoHelpList from './RiCoHelpList.vue'
import AcctNoSfxHelpList from './AcctNoSfxHelpList.vue'
import clone from 'clone'
export default {
  data() {
    return {
      labels: {
        scheme: 'Scheme',
        schemeDescFromHelpList: 'Scheme Description',
        schemeDescFromDB: 'Scheme Description',
        effDate: 'Effective Date',
        expDate: 'Expiry Date',
        polType: 'Policy Type',
        polTypeDescFromHelpList: 'Policy Type Description',
        polTypeDescFromDB: 'Policy Type Description',
        riType: 'RI Type',
        riCo: 'RI Company',
        riCoDescFromHelpList: 'RI Company Description',
        riCoDescFromDB: 'RI Company Description',
        acctNoSfx: 'Account No. Suffix',
        acctNoSfxDescFromHelpList: 'Account No. Suffix Description',
        acctNoSfxDescFromDB: 'Account No. Suffix Description',
        schemeDesc: 'Co/RI Panel Description',
        riSipct: 'RI Sum Insured %',
        comm1pct: 'Commission 1 %',
        comm2pct: 'Commission 2 %',
        comm2gn: 'Commission 2 Gross / Net',
        comm3pct: 'Commission 3 %',
        comm3gn: 'Commission 3 Gross / Net',
        cessStamp: 'Cession Stamp',
        riLevy: 'RI Levy',
        appAcqCost: 'Apply Acquisition Cost',
        recip: 'Reciprocal',
        tranComDate: 'Transaction / Commencement Date',
        bordereaux: 'Bordereaux',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      form: {
        scheme: '',
        effDate: new Date(),
        expDate: new Date(),
        schemeEffDate: undefined,
        schemeExpDate: undefined,
        polType: '',
        riType: '',
        riCo: '',
        acctNoSfx: '',
        schemeDesc: '',
        riSipct: 0,
        comm1pct: 0,
        comm2pct: 0,
        comm2gn: true,
        comm3pct: 0,
        comm3gn: true,
        cessStamp: 0,
        riLevy: 0,
        appAcqCost: false,
        recip: 'N',
        tranComDate: '',
        bordereaux: false
      },
      cloneQueryEffDate: '',
      cloneQueryExpDate: '',
      newMode: false,
      queryMode: false,
      reviewMode: false,
      resultStatus: undefined,
      riSIPctTotalAvai: 100,
      submitting: false,
      schemeList: false,
      polTypeList: false,
      riCoList: false,
      acctNoSfxList: false,
      schemeHelpList: false,
      polTypeHelpList: false,
      riCoHelpList: false,
      acctNoSfxHelpList: false,
      schemeDescFromHelpList: '',
      schemeDescFromDB: '',
      polTypeDescFromHelpList: '',
      polTypeDescFromDB: '',
      riCoDescFromHelpList: '',
      riCoDescFromDB: '',
      acctNoSfxDescFromHelpList: '',
      acctNoSfxDescFromDB: '',
      createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
      createdDate: new Date(),
      modifiedBy: '',
      modifiedDate: undefined,
      riTypeList: [
        { code: 'C', value: 'C - Coinsurance' },
        { code: 'D', value: 'D - Statutory' },
        { code: 'F', value: 'F - Facultative' },
        { code: 'T', value: 'T - Treaty' },
        { code: 'W', value: 'W - Quota Share' }
      ],
      comm2gnList: [
        { code: true, value: 'Gross' },
        { code: false, value: 'Net' }
      ],
      comm3gnList: [
        { code: true, value: 'Gross' },
        { code: false, value: 'Net' }
      ],
      recipList: [
        { code: 'N', value: 'No' },
        { code: 'Y', value: 'Yes' }
      ],
      tranComDateList: [
        { code: true, value: 'Transaction Date' },
        { code: false, value: 'Commencement Date' }
      ]
    }
  },
  methods: {
    async doInit() {
      await Promise.all([this.$store.dispatch('commonsInit')])
    },
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    },
    async doFetch() {
      const apiURI = this.menu.getCoRiPanel
      if (apiURI) {
        this.loading = true
        try {
          const result = await this.$http.put(apiURI, this.objParam)
          const { data } = result.data
          if (data) {
            this.form.scheme = data.CoRIPanel.scheme
            this.form.effDate = data.CoRIPanel.effDate
            this.form.expDate = data.CoRIPanel.expDate
            this.form.polType = data.CoRIPanel.polType
            this.form.riType = data.CoRIPanel.riType
            this.form.riCo = data.CoRIPanel.riCo
            this.form.acctNoSfx = data.CoRIPanel.acctNoSfx
            this.form.schemeDesc = data.CoRIPanel.schemeDesc
            this.form.riSipct = data.CoRIPanel.riSipct
            this.form.comm1pct = data.CoRIPanel.comm1pct
            this.form.comm2pct = data.CoRIPanel.comm2pct
            this.form.comm2gn = data.CoRIPanel.comm2gn
            this.form.comm3pct = data.CoRIPanel.comm3pct
            this.form.comm3gn = data.CoRIPanel.comm3gn
            this.form.cessStamp = data.CoRIPanel.cessStamp
            this.form.riLevy = data.CoRIPanel.riLevy
            this.form.appAcqCost = data.CoRIPanel.appAcqCost
            this.form.recip = data.CoRIPanel.recip
            this.form.tranComDate = data.CoRIPanel.tranComDate
            this.form.bordereaux = data.CoRIPanel.bordereaux
            this.createdBy = data.createdBy
            this.createdDate = data.createdDate
            this.modifiedBy = data.modifiedBy
            this.modifiedDate = data.modifiedDate
            this.schemeDescFromDB = data.schemeDesc
            this.polTypeDescFromDB = data.polTypeDesc
            this.riCoDescFromDB = data.riCoDesc
            this.acctNoSfxDescFromDB = data.acctNoSfxDesc
          }
          this.cloneQueryEffDate = clone(this.form.effDate)
          this.cloneQueryExpDate = clone(this.form.expDate)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'coRIPanelList' })
    },
    getHelpList(fieldName) {
      if (fieldName === 'scheme') {
        this.schemeHelpList = true
        this.schemeList = true
      } else if (fieldName === 'polType') {
        this.polTypeHelpList = true
        this.polTypeList = true
      } else if (fieldName === 'riCo') {
        this.riCoHelpList = true
        this.riCoList = true
      } else if (fieldName === 'acctNoSfx') {
        this.acctNoSfxHelpList = true
        this.acctNoSfxList = true
      }
    },
    doClose() {
      this.schemeHelpList = false
      this.schemeList = false
      this.polTypeHelpList = false
      this.polTypeList = false
      this.riCoHelpList = false
      this.riCoList = false
      this.acctNoSfxHelpList = false
      this.acctNoSfxList = false
    },
    doAssign(Obj) {
      if (Obj.from === 'scheme') {
        // console.log(Obj.scheme.expDate)
        this.form.polType = ''
        this.polTypeDescFromHelpList = ''
        this.form.scheme = Obj.scheme.scheme
        this.schemeDescFromHelpList = Obj.scheme.schemeDesc
        this.form.effDate = Obj.scheme.effDate
        this.form.schemeEffDate = Obj.scheme.effDate
        if (Obj.scheme.expDate === undefined) {
          this.form.expDate = removeTime(new Date())
          this.form.schemeExpDate = undefined
        } else {
          this.form.expDate = Obj.scheme.expDate
          this.form.schemeExpDate = Obj.scheme.expDate
        }
      }
      if (Obj.from === 'polType') {
        this.form.polType = Obj.polType.polType
        this.polTypeDescFromHelpList = Obj.polType.polTypeDesc
      }
      if (Obj.from === 'inter') {
        this.form.riCo = Obj.riCo.inter
        this.riCoDescFromHelpList = Obj.riCo.name
      }
      if (Obj.from === 'treaty') {
        this.form.riCo = Obj.riCo.ttyNo
        this.riCoDescFromHelpList = Obj.riCo.ttyDesc
      }
      if (Obj.from === 'acctNoSfx') {
        this.form.acctNoSfx = Obj.acctNoSfx.acctNoSfx
        this.acctNoSfxDescFromHelpList = Obj.acctNoSfx.description
      }
    },
    doSave() {
      this.coRIPanelRISIPctTotal().then((result1) => {
        if (this.resultStatus) {
          this.$validator.validateAll().then((result) => {
            if (result) {
              if (this.newMode) {
                this.doCreate()
              } else if (this.reviewMode) {
                this.doUpdate()
                // } else {
              }
            } else {
              this.promptFieldCheck()
            }
          })
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.createCoRIPanel
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.message(data)
            this.fieldMessage(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updateCoRIPanel
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response.data
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async coRIPanelRISIPctTotal() {
      const apiUrl = this.$store.state.commons.menu.coRIPanelRISIPctTotal
      // console.log(apiUrl)
      if (apiUrl) {
        try {
          const expand = {
            scheme: this.form.scheme,
            polType: this.form.polType,
            // effDate: formatDisplayDate(this.form.effDate),
            effDate: new Date(this.form.effDate),
            riType: this.form.riType,
            riSIPct: this.form.riSipct,
            riCo: this.form.riCo,
            mode: this.modeParam
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result) {
            // const data = result.data
            this.resultStatus = result.data.resultStatus
            this.riSIPctTotalAvai = result.data.riSIPctTotalAvai
          }
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.coRIPanelParam.menu
    },
    modeParam() {
      return this.$route.params.coRIPanelMode
    },
    objParam() {
      return this.$route.params.coRIPanelObjRes
    },
    filterRiTypeList() {
      return this.riTypeList.filter((list) => {
        return list.code === this.form.riType
      })
    },
    filterComm2gnList() {
      return this.comm2gnList.filter((list) => {
        return list.code === this.form.comm2gn
      })
    },
    filterComm3gnList() {
      return this.comm3gnList.filter((list) => {
        return list.code === this.form.comm3gn
      })
    },
    filterRecipList() {
      return this.recipList.filter((list) => {
        return list.code === this.form.recip
      })
    },
    filterTranComDateList() {
      return this.tranComDateList.filter((list) => {
        return list.code === this.form.tranComDate
      })
    },
    effDateValidation() {
      if (this.form.schemeEffDate !== undefined && this.form.schemeExpDate !== undefined) {
        return (
          'required|after_expdate:' +
          this.form.schemeExpDate +
          '|before_effdate:' +
          this.form.schemeEffDate +
          '|effdate_more_expdate:' +
          this.form.expDate
        )
      } else if (this.form.schemeEffDate !== undefined && this.form.schemeExpDate === undefined) {
        return 'required|before_effdate:' + this.form.schemeEffDate + '|effdate_more_expdate:' + this.form.expDate
      } else if (this.form.schemeEffDate === undefined && this.schemeExpDate === undefined) {
        return 'required|effdate_more_expdate:' + this.form.expDate
      } else {
        return 'required'
      }
    },
    expDateValidation() {
      if (this.form.schemeExpDate !== undefined && this.form.schemeExpDate !== '') {
        return 'after_expdate:' + this.form.schemeExpDate + '|expdate_less_effdate:' + this.form.effDate
      } else if (this.form.schemeEffDate !== undefined && this.form.schemeExpDate === undefined) {
        return 'before_effdate:' + this.form.schemeEffDate + '|expdate_less_effdate:' + this.form.effDate
      } else if (this.form.expDate !== undefined && this.form.expDate !== '') {
        return 'expdate_less_effdate:' + this.form.effDate
      } else {
        return ''
      }
    },
    riSIPctTotalValidation() {
      // if (this.riSIPctTotalAvai === 100 && this.newMode) {
      //   return 'mask_decimal:4|mask_min_value:0.0000|mask_max_value:100'
      // } else {
      //   return 'mask_decimal:4|mask_min_value:0.0000|mask_max_value:100|riSIPctTotal:' + this.riSIPctTotalAvai
      // }
      return 'mask_decimal:4|mask_min_value:0.0000|mask_max_value:100|riSIPctTotal:' + this.riSIPctTotalAvai
    }
  },
  watch: {
    'form.riType': function (newVal, oldVal) {
      if (this.newMode) {
        // check if newMode to prevent the data set in query/review mode to be changed
        if (newVal !== oldVal || newVal === '') {
          // if riType is changed, and is NOT Equal to Old Value
          // or it's empty (requires to backspace 3 times even if it's blank)
          this.form.riCo = ''
          this.riCoDescFromHelpList = ''
        }
      }
    }
  },
  mounted() {
    this.doInit()
  },
  created() {
    if (this.modeParam === 'create') {
      this.newMode = true
      // this.coRIPanelRISIPctTotal()
    } else if (!this.modeParam) {
      this.doCancel()
    } else {
      if (this.modeParam === 'query') {
        this.queryMode = true
      } else if (this.modeParam === 'review') {
        this.reviewMode = true
      }
      this.doFetch().then((result) => {
        this.coRIPanelRISIPctTotal()
      })
    }
    this.$validator.extend('riSIPctTotal', {
      getMessage(field, args) {
        return 'Total RI is more than 100%, must be less than ' + args[0] + '% (70158)'
      },
      validate(value, args) {
        let riSIPctAvai = args[0]
        // console.log('riSIPctAvai = ' + riSIPctAvai)
        // console.log(value)
        if (value !== null && value > Number(riSIPctAvai)) {
          // console.log('RISIPct > riSIPctAvai false')
          return false
        } else {
          // console.log('RISIPct < riSIPctAvai true')
          return true
        }
      }
    })
  },
  mixins: [commonAjaxMixin, MessageMixin],
  components: {
    SchemeHelpList,
    PolTypeHelpList,
    RiCoHelpList,
    AcctNoSfxHelpList
  }
}
</script>
